export default function HorizontalStickySubnav() {
  const header: HTMLElement | null = document.querySelector("header");
  const stickyScrollNav: HTMLDivElement | null = document.querySelector(".js-scroll-nav");
  const horizontalStickyNav: HTMLDivElement | null = document.querySelector(".js-horizontal-sticky-nav");
  const horizontalStickyNavWrapper: HTMLElement | null | undefined = document.querySelector(".js-horizontal-sticky-nav")?.parentElement;
  const headerMegaMenuItems: HTMLDivElement | null = document.querySelector(".js-desktop-header-menu");
  const megaMenuContainer = document.querySelector<HTMLElement>(".js-mega-menu-container");
  const hideOnScroll = megaMenuContainer?.getAttribute("data-hide-on-scroll")?.toLowerCase() === "true";

  if (horizontalStickyNav && header) {
    if (horizontalStickyNavWrapper) {
      horizontalStickyNavWrapper.classList.add("sticky", "global-transition");
      horizontalStickyNavWrapper.style.zIndex = "40";
    }

    const scrollToButtons = horizontalStickyNav.querySelectorAll<HTMLAnchorElement>(".js-scroll-to-btn");
    let isClickScrolling = false;
    let clickScrollUnlockTimeout: number | undefined;

    const getStickyScrollNavHeight = () => {
      if (!stickyScrollNav) {
        return 0;
      }

      const stickyScrollNavRect = stickyScrollNav.getBoundingClientRect();
      const isStickyScrollNavVisible = stickyScrollNavRect.top >= 0 && stickyScrollNavRect.height > 0;

      return isStickyScrollNavVisible ? stickyScrollNav.offsetHeight : 0;
    };

    const getScrollOffset = () => {
      return header.offsetHeight + getStickyScrollNavHeight() + horizontalStickyNav.offsetHeight;
    };

    const handleInit = () => {
      const offsetTop = header.offsetHeight + getStickyScrollNavHeight();

      if (horizontalStickyNavWrapper) {
        horizontalStickyNavWrapper.style.top = `${offsetTop}px`;
      }
    };

    const handleResetActiveButtons = () => {
      scrollToButtons.forEach((button) => {
        button.setAttribute("data-active", "false");
      });
    };

    const syncCarouselToButton = (button: Element) => {
      const carouselElement = document.querySelector<HTMLDivElement>(".js-horizontal-sticky-nav .swiper");

      if (!carouselElement || !(carouselElement as any).swiper) {
        return;
      }

      const swiperInstance = (carouselElement as any).swiper;
      const activeSlide = (button as HTMLElement).closest(".swiper-slide");

      if (!activeSlide) {
        return;
      }

      const allSlides = carouselElement.querySelectorAll(".swiper-slide");
      const slideIndex = Array.from(allSlides).indexOf(activeSlide);

      if (slideIndex !== -1) {
        swiperInstance.slideTo(slideIndex);
      }
    };

    const setActiveButton = (sectionId: string) => {
      const button = horizontalStickyNav.querySelector(`[data-target-id="#${sectionId}"]`);

      if (!button) {
        return;
      }

      handleResetActiveButtons();
      button.setAttribute("data-active", "true");
      syncCarouselToButton(button);
    };

    const getOrderedNavSections = () => {
      const sections: HTMLElement[] = [];

      scrollToButtons.forEach((button) => {
        const targetId = button.getAttribute("data-target-id");

        if (!targetId) {
          return;
        }

        const cleanId = targetId.startsWith("#") ? targetId.slice(1) : targetId;
        const section = document.getElementById(cleanId);

        if (section && !sections.includes(section)) {
          sections.push(section);
        }
      });

      return sections.sort((firstSection, secondSection) => {
        if (firstSection === secondSection) {
          return 0;
        }

        const secondFollowsFirst =
          firstSection.compareDocumentPosition(secondSection) & Node.DOCUMENT_POSITION_FOLLOWING;

        return secondFollowsFirst ? -1 : 1;
      });
    };

    const updateActiveSection = () => {
      if (isClickScrolling) {
        return;
      }

      const scrollOffset = getScrollOffset();
      const navSections = getOrderedNavSections();

      if (navSections.length === 0) {
        return;
      }

      const topmostSectionTop = Math.min(
        ...navSections.map((section) => section.getBoundingClientRect().top),
      );

      if (topmostSectionTop > scrollOffset) {
        handleResetActiveButtons();
        return;
      }

      let activeSectionId: string | null = null;

      for (const section of navSections) {
        if (!section.id) {
          continue;
        }

        const sectionRect = section.getBoundingClientRect();

        if (sectionRect.top <= scrollOffset + 5) {
          activeSectionId = section.id;
        } else {
          break;
        }
      }

      if (activeSectionId) {
        setActiveButton(activeSectionId);
      } else {
        handleResetActiveButtons();
      }
    };

    const handleScrollTo = (targetId: string) => {
      const cleanId = targetId.startsWith("#") ? targetId.slice(1) : targetId;
      const section = document.getElementById(cleanId);

      if (!section) {
        return;
      }

      const sectionOffset = section.getBoundingClientRect().top + window.scrollY;
      const scrollOffset = getScrollOffset();

      window.scrollTo({
        top: sectionOffset - scrollOffset,
        behavior: "smooth",
      });
    };

    const unlockClickScrollingAfterSettle = () => {
      const onScroll = () => {
        window.clearTimeout(clickScrollUnlockTimeout);
        clickScrollUnlockTimeout = window.setTimeout(() => {
          isClickScrolling = false;
          window.removeEventListener("scroll", onScroll);
          updateActiveSection();
        }, 150);
      };

      window.addEventListener("scroll", onScroll, { passive: true });

      // Fallback if smooth scroll is interrupted or already at destination
      window.clearTimeout(clickScrollUnlockTimeout);
      clickScrollUnlockTimeout = window.setTimeout(() => {
        isClickScrolling = false;
        window.removeEventListener("scroll", onScroll);
        updateActiveSection();
      }, 1500);
    };

    const handleOnClick = (event: Event) => {
      const button = event.currentTarget as HTMLAnchorElement;
      const targetId = button.getAttribute("data-target-id");

      if (!targetId) {
        return;
      }

      event.preventDefault();
      isClickScrolling = true;
      handleResetActiveButtons();
      button.setAttribute("data-active", "true");
      syncCarouselToButton(button);
      handleScrollTo(targetId);
      unlockClickScrollingAfterSettle();
    };

    scrollToButtons.forEach((button) => {
      button.addEventListener("click", handleOnClick);
    });

    setTimeout(() => {      
      handleInit();
      updateActiveSection();
    }, 300);

    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", () => {
      handleInit();
      updateActiveSection();
    });

    if (horizontalStickyNavWrapper) {
      let lastScrollY = window.scrollY;
      let isResizing = false;
      let resizeTimeout: number;

      const getInitialWrapperPosition = () => {
        const rect = horizontalStickyNavWrapper.getBoundingClientRect();
        return rect.top + window.scrollY;
      };

      const getStickyThreshold = () => {
        let stickyTop = header.offsetHeight + getStickyScrollNavHeight();
        const wrapperOffsetTop = getInitialWrapperPosition();
        return wrapperOffsetTop - stickyTop;
      };

      window.addEventListener("resize", () => {
        isResizing = true;
        clearTimeout(resizeTimeout);

        resizeTimeout = window.setTimeout(() => {
          isResizing = false;
          lastScrollY = window.scrollY;
        }, 200);
      });

      window.addEventListener(
        "scroll",
        () => {
          if (isResizing) {
            return;
          }

          const stickyThreshold = getStickyThreshold();
          const currentScrollY = window.scrollY;

          if (currentScrollY < stickyThreshold) {
            horizontalStickyNavWrapper.style.transform = "translateY(0)";
            return;
          }

          if (window.innerWidth < 768 || headerMegaMenuItems == null) {
            horizontalStickyNavWrapper.style.transform = "translateY(0)";
            return;
          }

          if (currentScrollY > lastScrollY && hideOnScroll) {
            horizontalStickyNavWrapper.style.transform = "translateY(-50px)";
            horizontalStickyNavWrapper.style.zIndex = "50";
          } else if (currentScrollY < lastScrollY) {
            horizontalStickyNavWrapper.style.transform = "translateY(0)";
            horizontalStickyNavWrapper.style.zIndex = "40";
          }

          lastScrollY = currentScrollY;
        },
        { passive: true },
      );
    }
  }
}
